import { Close, CropOriginal, MoreHoriz } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { object, string } from 'yup';
import { useUpdateMediaItem } from '~/api/media-items';
import { OutlinedIconButton } from '~/components/button';
import { InlineEditTitle, Toolbar } from '~/components/toolbar';
import { useLocation } from '~/hooks/location';
import { useMoreActions } from '~/hooks/table';
import { MediaItemMenu } from './MediaItemMenu';
import type { MediaItemToolbarProps__MediaItem } from './MediaItemToolbar.generated';

/* GraphQL */ `#graphql
fragment MediaItemToolbarProps__MediaItem on MediaItem {
  canDestroy {
    ...AuthorizationResult
  }
  contentItemCount
  designCount
  file {
    id
    uri
  }
  id
  kind
  name
  originalFile {
    downloadUri
    id
    originalFilename
  }
  status
}
`;
export interface MediaItemToolbarProps {
  mediaItem: MediaItemToolbarProps__MediaItem;
  onReplace: () => void;
}

export const MediaItemToolbar = ({ mediaItem, onReplace }: MediaItemToolbarProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [updateMediaItem] = useUpdateMediaItem();

  const [moreMenuProps, moreActionProps, moreTableActions] = useMoreActions<typeof mediaItem>();

  const handleClose = () => {
    navigate(location.state?.returnTo ?? '..', { replace: true });
  };

  return (
    <>
      <Toolbar
        pageTitle={mediaItem.name}
        actions={
          <Box sx={{ display: 'flex', gap: 3 }}>
            <OutlinedIconButton
              label={t('common.buttons.more_actions')}
              onClick={moreTableActions(mediaItem).onClick}
              variant="outlined"
              size="large"
            >
              <MoreHoriz />
            </OutlinedIconButton>
            <OutlinedIconButton
              label={t('common.actions.close')}
              onClick={handleClose}
              variant="outlined"
              size="large"
            >
              <Close />
            </OutlinedIconButton>
          </Box>
        }
        titleIcon={<CropOriginal />}
        titleEditor={
          <InlineEditTitle
            inputId={`media-item-${mediaItem.id}-name`}
            value={mediaItem.name}
            update={(name: string) =>
              updateMediaItem({
                variables: { mediaItemId: mediaItem.id, patch: { name } },
              })
            }
            tooltip={t('pages.content.media_details.edit_name_tooltip')}
            validationSchema={object({
              name: string().required(t('common.validation.name_required')).trim(),
            })}
          />
        }
      />

      <MediaItemMenu
        item={mediaItem}
        moreActionProps={moreActionProps}
        onReplace={onReplace}
        {...moreMenuProps}
      />
    </>
  );
};
